import { useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useApp } from "../contexts/AppContext";
import { useLanguage } from "../contexts/LanguageContext";
import { RootState } from "../store";
import { useGetProjectsQuery } from "../store/api/projects";
import { useGetWorkspaceMembersQuery } from "../store/api/workspaces";
import { useGetWorkspaceProjectTasksQuery } from "../store/api/tasks";
import { ProjectTask, TaskInstance } from "../types";
import { isOverdue } from "../utils/taskGenerator";
import { AlertCircle, Clock, Calendar, CheckCircle2, TrendingUp, BarChart3, UserPlus, ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, format, subMonths } from "date-fns";
import { ja } from "date-fns/locale";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { WORKSPACE_DEFAULT_TIMEZONE } from "../utils/constants";

export default function Dashboard() {
  const { currentUser } = useApp();
  const { t, language } = useLanguage();
  
  // Get current workspace from Redux
  const currentWorkspace = useSelector((state: RootState) => state.workspace.currentWorkspace);
  const workspaceId = currentWorkspace?.id || '';
  const workspaceTimeZone = currentWorkspace?.timezone || WORKSPACE_DEFAULT_TIMEZONE;

  // Fetch projects from API
  const { data: projects = [], isLoading: isLoadingProjects } = useGetProjectsQuery(
    workspaceId,
    { skip: !workspaceId }
  );

  // Fetch workspace users from API
  const { data: users = [], isLoading: isLoadingUsers } = useGetWorkspaceMembersQuery(
    workspaceId,
    { skip: !workspaceId }
  );

  // Get active (non-archived) projects
  const activeProjects = useMemo(() => {
    return projects.filter(p => !p.isArchived);
  }, [projects]);

  const { data: workspaceTasks = [], isLoading: isLoadingWorkspaceTasks } = useGetWorkspaceProjectTasksQuery(
    { workspaceId },
    { skip: !workspaceId }
  );

  const activeProjectIds = useMemo(() => {
    return new Set(activeProjects.map((project) => project.id));
  }, [activeProjects]);

  const filteredProjectTasks = useMemo(() => {
    return workspaceTasks.filter((task) => activeProjectIds.has(task.projectId));
  }, [workspaceTasks, activeProjectIds]);

  // Convert ProjectTask[] to TaskInstance format
  const taskInstances: TaskInstance[] = useMemo(() => {
    return filteredProjectTasks.map(task => {
      const dueDate = task.dueDate ? new Date(task.dueDate) : new Date();
      const completedAt = task.completedAt ? new Date(task.completedAt) : undefined;
      const occurrenceDate = task.createdAt ? new Date(task.createdAt) : new Date();
      
      return {
        ...task,
        dueDate,
        completedAt,
        routineTaskId: task.isRepeatable ? task.id : null,
        occurrenceDate,
      } as TaskInstance;
    });
  }, [filteredProjectTasks]);
  const [periodMonths, setPeriodMonths] = useState<number>(6); // 表示期間（月数）
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  
  // Count mode: 'completed' = completed tasks, 'assigned' = all assigned tasks
  const [countMode, setCountMode] = useState<'completed' | 'assigned'>('completed');

  const isLoading = isLoadingProjects || isLoadingUsers || isLoadingWorkspaceTasks;

  // デフォルトで全ユーザーを展開
  useEffect(() => {
    if (users.length > 0 && expandedUsers.size === 0) {
      setExpandedUsers(new Set(users.map(u => u.id)));
    }
  }, [users]);

  // ゲストユーザーの場合、自分に割り当てられたタスクのみを取得
  const visibleTasks = useMemo(() => {
    if (currentUser?.role === 'guest' && currentUser.projectIds) {
      return taskInstances.filter(task => 
        currentUser.projectIds?.includes(task.projectId) &&
        task.assigneeIds.includes(currentUser.id)
      );
    }
    return taskInstances;
  }, [taskInstances, currentUser]);

  // サマリー統計
  const summary = useMemo(() => {
    const now = new Date();
    const todayStart = startOfDay(now);
    const todayEnd = endOfDay(now);
    const weekEnd = endOfWeek(now, { weekStartsOn: 1 });

    const pendingTasks = visibleTasks.filter((t) => t.status === "pending");

    return {
      total: pendingTasks.length,
      overdue: pendingTasks.filter((t) => isOverdue(t, workspaceTimeZone)).length,
      today: pendingTasks.filter(
        (t) => t.dueDate >= todayStart && t.dueDate <= todayEnd
      ).length,
      thisWeek: pendingTasks.filter(
        (t) => t.dueDate >= now && t.dueDate <= weekEnd
      ).length,
    };
  }, [visibleTasks]);

  // 担当者別統計
  const userStats = useMemo(() => {
    return users.map((user) => {
      const userTasks = taskInstances.filter(
        (t) => t.assigneeIds.includes(user.id) && t.status === "pending"
      );
      const now = new Date();
      const todayStart = startOfDay(now);
      const todayEnd = endOfDay(now);
      const weekEnd = endOfWeek(now, { weekStartsOn: 1 });

      // 過去30日間の完了率
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const recentTasks = taskInstances.filter(
        (t) =>
          t.assigneeIds.includes(user.id) &&
          t.occurrenceDate && t.occurrenceDate >= thirtyDaysAgo
      );
      const completedRecent = recentTasks.filter((t) => t.status === "completed");
      const completionRate =
        recentTasks.length > 0
          ? Math.round((completedRecent.length / recentTasks.length) * 100)
          : 0;

      return {
        user,
        pending: userTasks.length,
        overdue: userTasks.filter((t) => isOverdue(t, workspaceTimeZone)).length,
        today: userTasks.filter(
          (t) => t.dueDate >= todayStart && t.dueDate <= todayEnd
        ).length,
        thisWeek: userTasks.filter((t) => t.dueDate >= now && t.dueDate <= weekEnd)
          .length,
        completionRate,
      };
    });
  }, [taskInstances, users]);

  // プロジェクト別統計
  const projectStats = useMemo(() => {
    return activeProjects.map((project) => {
      const projectTasks = taskInstances.filter(
        (t) => t.projectId === project.id && t.status === "pending"
      );
      
      // 過去30日間の完了率
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const recentTasks = taskInstances.filter(
        (t) =>
          t.projectId === project.id &&
          t.occurrenceDate && t.occurrenceDate >= thirtyDaysAgo
      );
      const completedRecent = recentTasks.filter((t) => t.status === "completed");
      const completionRate =
        recentTasks.length > 0
          ? Math.round((completedRecent.length / recentTasks.length) * 100)
          : 0;

      return {
        project,
        pending: projectTasks.length,
        overdue: projectTasks.filter((t) => isOverdue(t, workspaceTimeZone)).length,
        completionRate,
      };
    });
  }, [taskInstances, activeProjects]);

  // 月ごとのタスク集計（ユーザー別・プロジェクト別）
  // countMode: 'completed' = 完了タスク, 'assigned' = 割り当てタスク（作成日ベース）
  const monthlyCompletionStats = useMemo(() => {
    const now = new Date();
    const months = [];
    
    // 過去N ヶ月分のデータを生成
    for (let i = periodMonths - 1; i >= 0; i--) {
      const monthDate = subMonths(now, i);
      const monthStart = startOfMonth(monthDate);
      const monthEnd = i === 0 ? now : startOfMonth(subMonths(now, i - 1));
      
      months.push({
        date: monthDate,
        monthKey: format(monthDate, 'yyyy-MM'),
        monthLabel: language === 'ja' 
          ? format(monthDate, 'M月', { locale: ja })
          : format(monthDate, 'MMM'),
        start: monthStart,
        end: monthEnd,
      });
    }

    // 月ごとのユーザー別・プロジェクト別タスク数を集計
    const stats = months.map((month) => {
      // Filter tasks based on count mode
      const monthTasks = countMode === 'completed'
        ? taskInstances.filter(
            (t) => t.status === "completed" && 
            t.completedAt && 
            t.completedAt >= month.start && 
            t.completedAt < month.end
          )
        : taskInstances.filter(
            (t) => t.occurrenceDate && 
            t.occurrenceDate >= month.start && 
            t.occurrenceDate < month.end
          );

      const userProjectStats: Record<string, Record<string, number>> = {};
      
      // ユーザー別・プロジェクト別にカウント
      monthTasks.forEach((task) => {
        task.assigneeIds.forEach((userId) => {
          if (!userProjectStats[userId]) {
            userProjectStats[userId] = {};
          }
          if (!userProjectStats[userId][task.projectId]) {
            userProjectStats[userId][task.projectId] = 0;
          }
          userProjectStats[userId][task.projectId]++;
        });
      });

      return {
        month: month.monthLabel,
        monthKey: month.monthKey,
        total: monthTasks.length,
        userProjectStats,
      };
    });

    return stats;
  }, [taskInstances, language, periodMonths, countMode]);

  // ユーザー別の月次完了タスク数（チャート用）
  const monthlyUserChartData = useMemo(() => {
    return monthlyCompletionStats.map((stat) => {
      const data: any = { month: stat.month };
      
      users.forEach((user) => {
        const userTotal = stat.userProjectStats[user.id] 
          ? Object.values(stat.userProjectStats[user.id]).reduce((sum, count) => sum + count, 0)
          : 0;
        data[user.name] = userTotal;
      });
      
      return data;
    });
  }, [monthlyCompletionStats, users]);

  // プロジェクト別の月次完了タスク数（チャート用）
  const monthlyProjectChartData = useMemo(() => {
    return monthlyCompletionStats.map((stat) => {
      const data: any = { month: stat.month };
      
      activeProjects.forEach((project) => {
        let projectTotal = 0;
        Object.values(stat.userProjectStats).forEach((userStats) => {
          if (userStats[project.id]) {
            projectTotal += userStats[project.id];
          }
        });
        data[project.name] = projectTotal;
      });
      
      return data;
    });
  }, [monthlyCompletionStats, activeProjects]);

  // カラーパレット
  const userColors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
  const projectColors = ['#06b6d4', '#14b8a6', '#84cc16', '#f97316', '#a855f7', '#f43f5e'];

  if (isLoading) {
    return (
      <div className="max-w-[1600px] mx-auto px-6 py-8">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          <span className="ml-2 text-gray-600">
            {language === 'ja' ? '読み込み中...' : 'Loading...'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] mx-auto px-6 py-8">
      <div className="mb-8">
        <p className="text-gray-600">
          {language === 'ja' ? 'タスクの進捗状況と重要な指標をご確認いただけます' : 'View task progress and important metrics'}
        </p>
      </div>

      {/* メインコンテンツ */}
      {currentUser?.role !== 'guest' && (
        <div className="space-y-8">
          {/* 月別詳細テーブル */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-gray-900">{t('dashboard.monthlyDetailTable')}</h2>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setExpandedUsers(new Set(users.map(u => u.id)))}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    {language === 'ja' ? '全展開' : 'Expand All'}
                  </button>
                  <button
                    onClick={() => setExpandedUsers(new Set())}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    {language === 'ja' ? '全クローズ' : 'Collapse All'}
                  </button>
                </div>
                {/* Count Type Selector - Hidden but can be re-enabled
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">
                    {language === 'ja' ? '集計タイプ:' : 'Count Type:'}
                  </label>
                  <Select 
                    value={countMode} 
                    onValueChange={(value) => setCountMode(value as 'completed' | 'assigned')}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="assigned">
                        {language === 'ja' ? '割り当てタスク' : 'Assigned Tasks'}
                      </SelectItem>
                      <SelectItem value="completed">
                        {language === 'ja' ? '完了タスク' : 'Completed Tasks'}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                */}
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">
                    {language === 'ja' ? '表示期間:' : 'Period:'}
                  </label>
                  <Select 
                    value={periodMonths.toString()} 
                    onValueChange={(value) => setPeriodMonths(parseInt(value))}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">
                        {language === 'ja' ? '過去3ヶ月' : 'Last 3 months'}
                      </SelectItem>
                      <SelectItem value="6">
                        {language === 'ja' ? '過去6ヶ月' : 'Last 6 months'}
                      </SelectItem>
                      <SelectItem value="12">
                        {language === 'ja' ? '過去12ヶ月' : 'Last 12 months'}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-gray-900 sticky left-0 bg-gray-50 z-10">
                        {t('dashboard.user')} / {language === 'ja' ? 'プロジェクト' : 'Project'}
                      </th>
                      {monthlyCompletionStats.map((monthStat) => (
                        <th key={monthStat.monthKey} className="px-4 py-3 text-center text-gray-900 min-w-[80px]">
                          {monthStat.month}
                        </th>
                      ))}
                      <th className="px-4 py-3 text-center text-gray-900 bg-blue-50 min-w-[80px]">
                        {t('dashboard.total')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {users.map((user) => {
                      const isExpanded = expandedUsers.has(user.id);
                      const isGuest = user.role === 'guest';
                      
                      // ユーザーの月別合計を計算
                      const userMonthlyTotals = monthlyCompletionStats.map((monthStat) => {
                        const userStats = monthStat.userProjectStats[user.id] || {};
                        return Object.values(userStats).reduce((sum, count) => sum + count, 0);
                      });
                      
                      const userGrandTotal = userMonthlyTotals.reduce((sum, count) => sum + count, 0);
                      
                      return (
                        <>
                          {/* ユーザー行 */}
                          <tr 
                            key={user.id} 
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => {
                              const newExpanded = new Set(expandedUsers);
                              if (isExpanded) {
                                newExpanded.delete(user.id);
                              } else {
                                newExpanded.add(user.id);
                              }
                              setExpandedUsers(newExpanded);
                            }}
                          >
                            <td className="px-4 py-3 sticky left-0 bg-white">
                              <div className="flex items-center gap-2">
                                {isExpanded ? (
                                  <ChevronDown className="w-4 h-4 text-gray-400" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-gray-400" />
                                )}
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-sm">
                                  {user.name.charAt(0)}
                                </div>
                                <div className="flex items-center gap-1">
                                  <span className="text-gray-900">{user.name}</span>
                                  {isGuest && (
                                    <UserPlus className="w-4 h-4 text-blue-500" />
                                  )}
                                </div>
                              </div>
                            </td>
                            {userMonthlyTotals.map((total, index) => (
                              <td key={index} className="px-4 py-3 text-center text-gray-900">
                                {total > 0 ? total : '-'}
                              </td>
                            ))}
                            <td className="px-4 py-3 text-center text-gray-900 bg-blue-50">
                              {userGrandTotal > 0 ? userGrandTotal : '-'}
                            </td>
                          </tr>
                          
                          {/* プロジェクト行（展開時） */}
                          {isExpanded && activeProjects.map((project) => {
                            // このユーザー＆プロジェクトの月別データを取得
                            const projectMonthlyData = monthlyCompletionStats.map((monthStat) => {
                              const userStats = monthStat.userProjectStats[user.id] || {};
                              return userStats[project.id] || 0;
                            });
                            
                            const projectTotal = projectMonthlyData.reduce((sum, count) => sum + count, 0);
                            
                            // このユーザーがこのプロジェクトでタスクを持っている場合のみ表示
                            if (projectTotal === 0) return null;
                            
                            return (
                              <tr key={`${user.id}-${project.id}`} className="bg-gray-50">
                                <td className="px-4 py-2 pl-16 sticky left-0 bg-gray-50">
                                  <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center">
                                      <span className="text-xs text-blue-600">P</span>
                                    </div>
                                    <span className="text-sm text-gray-700">{project.name}</span>
                                  </div>
                                </td>
                                {projectMonthlyData.map((count, index) => (
                                  <td key={index} className="px-4 py-2 text-center text-sm text-gray-700">
                                    {count > 0 ? count : '-'}
                                  </td>
                                ))}
                                <td className="px-4 py-2 text-center text-sm text-gray-700 bg-blue-50">
                                  {projectTotal}
                                </td>
                              </tr>
                            );
                          })}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
